import {
  Box,
  Button,
  Circle,
  Flex,
  GridItem,
  HStack,
  Icon,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { MdExpandMore, MdErrorOutline } from "react-icons/md";
import { AnalystProto, ColumnScopeProto } from "build-ts/analyst";
import { ErrorDetailProto, Header, MetricProto } from "build-ts/metadata";
import { FilterOperator } from "build-ts/enums";
import React, { useMemo, useState } from "react";
import { EntityKind, getKindIcon, SCROLLBAR_SX } from "./DataScopeLeftPanel";
import { KGColumnProto } from "build-ts/knowledege_graph";
import EntityRightPanelCard from "./EntityRightPanelCard";
import { AppIcons } from "constants/icons";

// ─── Props ────────────────────────────────────────────────────────────────────

interface DataScopeRightPanelProps {
  selectedMetricHeaders: Header[];
  onRemoveMetric: (metricId: string) => void;

  selectedAttributes: Header[];
  selectedMeasures: Header[];
  selectedTimeframes: Header[];

  selectedBenchmarks: Header[];
  onRemoveBenchmark: (header: Header) => void;

  columnScopes: ColumnScopeProto[];

  sessionNewIds: Set<string>;
  loadingAttributeIds: Set<string>;

  onSaveConstraint: (index: number, operator: FilterOperator, values: string[]) => void;
  onRemoveColumnScope: (header: Header, kind: EntityKind) => void;

  isRefreshingAttributeValues: boolean;
  isSavingConstraint: boolean;
  attributeValueErrors: Map<string, ErrorDetailProto[]>;

  isLoadingScope?: boolean;
  originalAnalyst?: AnalystProto;
  kgColumnProtoMap: Map<string, KGColumnProto>;
  dataModelList: Header[];
  metricProtoMap: Map<string, MetricProto>;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const CollapsibleSection: React.FC<{
  type: EntityKind;
  title: string;
  count: number;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ type, title, count, isOpen, onToggle, children }) => (
  <Box w="100%" mt={3}>
    <Flex
      as="button"
      type="button"
      w="100%"
      align="center"
      justify="space-between"
      onClick={onToggle}
      borderBottom="1px solid"
      borderColor="gray.400"
      pb={1.5}
      cursor="pointer"
      _hover={{ opacity: 0.75 }}
      bg="transparent"
    >
      <HStack spacing={1.5} align="center">
        <Icon as={getKindIcon(type)} boxSize={4} color="gray.900" flexShrink={0} aria-hidden />
        <Text
          fontSize="xs"
          fontWeight="700"
          color="gray.900"
          letterSpacing="0.08em"
          textTransform="uppercase"
          mb={0}
          textAlign="left"
        >
          {title}
        </Text>
        <Circle
          size="16px"
          bg="purple.600"
          color="white"
          fontSize="2xs"
          fontWeight="600"
          mb={0}
        >
          {count}
        </Circle>
      </HStack>
      <Icon
        as={MdExpandMore}
        boxSize={4}
        color="gray.500"
        transform={isOpen ? "rotate(0deg)" : "rotate(-90deg)"}
        transition="transform 0.18s ease"
        flexShrink={0}
      />
    </Flex>
    {isOpen && <Box mt={2}>{children}</Box>}
  </Box>
);


// ─── Inline attribute constraint editor ──────────────────────────────────────

interface InlineConstraintEditorProps {
  scope: ColumnScopeProto;
  isLoadingValues?: boolean;
  onSave: (operator: FilterOperator, values: string[]) => void;
  onCancel: () => void;
}

const InlineConstraintEditor: React.FC<InlineConstraintEditorProps> = ({
  scope,
  isLoadingValues = false,
  onSave,
  onCancel,
}) => {
  const [selectedValues, setSelectedValues] = useState<string[]>(scope.values ?? []);

  const availableValues = scope.available_values ?? [];

  const toggleValue = (val: string) => {
    setSelectedValues(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
    );
  };

  return (
    <Box borderTopWidth="1px" borderColor="gray.200" pt={2} mt={1}>
      <VStack spacing={2} align="stretch">
        {isLoadingValues ? (
          <Flex justify="center" py={2}>
            <Spinner size="sm" color="purple.500" />
          </Flex>
        ) : availableValues.length === 0 ? (
          <Text fontSize="xs" color="gray.400" textAlign="center" py={1} mb={0}>
            {scope.kg_column?.cardinality === 1
              ? "No values loaded yet. Use the refresh button to fetch available values."
              : "No values available. This column may have high cardinality."}
          </Text>
        ) : (
          <Flex flexWrap="wrap" gap={1.5} pt={1}>
            {availableValues.map(val => {
              const isSelected = selectedValues.includes(val);
              return (
                <Box
                  key={val}
                  as="button"
                  type="button"
                  onClick={() => toggleValue(val)}
                  px={2.5}
                  py={0.5}
                  borderRadius="full"
                  fontSize="xs"
                  fontWeight="500"
                  lineHeight="tall"
                  bg={isSelected ? "purple.50" : "gray.100"}
                  color={isSelected ? "purple.700" : "gray.700"}
                  borderWidth="1px"
                  borderColor={isSelected ? "purple.300" : "gray.400"}
                  cursor="pointer"
                  transition="all 0.12s"
                  _hover={{ opacity: 0.75 }}
                >
                  {val}
                </Box>
              );
            })}
          </Flex>
        )}

        <HStack justify="flex-end" spacing={2} pt={1}>
          <Button size="xs" variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button
            size="xs"
            colorScheme="purple"
            onClick={() => onSave(selectedValues.length > 0 ? FilterOperator.EQ : FilterOperator.NO_FILTER_OP, selectedValues)}
          >
            Save
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const DataScopeRightPanel: React.FC<DataScopeRightPanelProps> = ({
  selectedMetricHeaders,
  onRemoveMetric,
  selectedAttributes,
  selectedMeasures,
  selectedTimeframes,
  selectedBenchmarks,
  onRemoveBenchmark,
  columnScopes,
  sessionNewIds,
  loadingAttributeIds,
  onSaveConstraint,
  onRemoveColumnScope,
  isRefreshingAttributeValues,
  isSavingConstraint,
  attributeValueErrors,
  isLoadingScope = false,
  kgColumnProtoMap,
  dataModelList,
  metricProtoMap,
}) => {
  const [constrainOpenId, setConstrainOpenId] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const toggleSection = (id: string) =>
    setCollapsedSections(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const isSectionOpen = (id: string) => !collapsedSections.has(id);

  // Block all removes only while a refresh API call is in-flight
  const isScopeLocked = isRefreshingAttributeValues;

  const isSessionNew = (id: string) => sessionNewIds.has(id);

  // Attribute column scopes (attributes only, in the same order as columnScopes)
  const attrScopes = useMemo(
    () => columnScopes.filter(
      s => s.kg_column?.header?.id && selectedAttributes.some(a => a.id === s.kg_column?.header?.id)
    ),
    [columnScopes, selectedAttributes]
  );

  const selectedCount =
    selectedMetricHeaders.length +
    selectedMeasures.length +
    selectedAttributes.length +
    selectedTimeframes.length +
    selectedBenchmarks.length;

  // ─── Card renderers ───────────────────────────────────────────────────────

  const renderMetricCards = (metrics: Header[]) => (
    <SimpleGrid columns={3} spacing={2} mb={2} w="100%">
      {metrics.map(m => {
        if (!m.id) return null;
        return (
          <EntityRightPanelCard
            key={m.id}
            header={m}
            entityKind="metric"
            isSessionNew={isSessionNew(m.id)}
            title={m.name || "—"}
            onRemove={() => onRemoveMetric(m.id!)}
            isRemoveDisabled={isScopeLocked}
            kgColumnProtoMap={kgColumnProtoMap}
            dataModelList={dataModelList}
            metricProtoMap={metricProtoMap}
          />
        );
      })}
    </SimpleGrid>
  );

  const renderMeasureCards = (measures: Header[]) => (
    <SimpleGrid columns={3} spacing={2} mb={2} w="100%">
      {measures.map(m => {
        if (!m.id) return null;
        return (
          <EntityRightPanelCard
            key={m.id}
            header={m}
            entityKind="measure"
            isSessionNew={isSessionNew(m.id)}
            title={m.name || "—"}
            onRemove={() => onRemoveColumnScope(m, "measure")}
            isRemoveDisabled={isScopeLocked}
            kgColumnProtoMap={kgColumnProtoMap}
            dataModelList={dataModelList}
            metricProtoMap={metricProtoMap}
          />
        );
      })}
    </SimpleGrid>
  );

  const renderAttributeCards = (scopes: ColumnScopeProto[]) => (
    <SimpleGrid columns={3} spacing={2} mb={2} w="100%">
      {scopes.map(scope => {
        const h = scope.kg_column?.header;
        if (!h?.id) return null;
        const attrId = h.id;
        const isOpen = constrainOpenId === attrId;
        const nRestricted = scope.values?.length ?? 0;
        const totalAvailable = scope.available_values?.length ?? 0;
        const isHighCardinality = scope.kg_column?.cardinality !== 1;
        const scopeIndex = columnScopes.findIndex(f => f?.kg_column?.header?.id === attrId);

        const isLoadingValues = loadingAttributeIds.has(attrId);

        // Subtitle pill — hidden while the inline editor is open
        const subtitleContent = isOpen ? undefined : isLoadingValues ? (
          <HStack spacing={1} px={1} align="center">
            <Spinner size="xs" color="gray.400" flexShrink={0} />
            <Text fontSize="xs" fontWeight="500" color="gray.400" mb={0} lineHeight="short">
              Loading values…
            </Text>
          </HStack>
        ) : isHighCardinality ? (
          // Non-interactive label for high-cardinality columns
          <Text fontSize="xs" fontWeight="500" color="gray.400" mb={0} lineHeight="short" px={1} textAlign="left">
            Too many values
          </Text>
        ) : (
          // Clickable pill that opens the constraint editor
          <Tooltip
            isDisabled={nRestricted === 0}
            label={(scope.values || []).join(", ")}
            hasArrow
            openDelay={250}
            maxW="320px"
            placement="top"
            whiteSpace="pre-wrap"
          >
            <Box
              as="button"
              type="button"
              w="100%"
              textAlign="left"
              fontSize="xs"
              fontWeight="600"
              lineHeight="short"
              color="gray.700"
              bg="gray.50"
              px={2}
              py={0.5}
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.300"
              cursor="pointer"
              mb={0}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                setConstrainOpenId(attrId);
              }}
              _hover={{ bg: "gray.100", borderColor: "gray.400" }}
              aria-label="Edit value constraints"
            >
              {nRestricted > 0 ? (
                <Text as="span" display="block" w="100%" mb={0} noOfLines={1} textAlign="left">
                  {nRestricted}/{totalAvailable} values selected
                </Text>
              ) : (
                <Text as="span" display="block" w="100%" mb={0} noOfLines={1} textAlign="left">
                  {totalAvailable} values available
                </Text>
              )}
            </Box>
          </Tooltip>
        );

        const errors = attributeValueErrors.get(attrId);
        const errorIcon = errors?.length ? (
          <Tooltip
            label={
              <VStack align="start" spacing={0.5}>
                {errors.map((e, i) => (
                  <Text key={i} fontSize="xs" mb={0} lineHeight="short">
                    {e.error_name ? <strong>{e.error_name}: </strong> : null}
                    {e.error_msg ?? "Unknown error"}
                  </Text>
                ))}
              </VStack>
            }
            hasArrow
            placement="top"
            maxW="280px"
            bg="red.600"
            color="white"
            px={2}
            py={1.5}
            borderRadius="md"
          >
            <span>
              <Icon as={MdErrorOutline} boxSize={4} color="red.500" cursor="default" />
            </span>
          </Tooltip>
        ) : undefined;

        return (
          <GridItem key={attrId} colSpan={isOpen ? 3 : 1} w="100%">
            <EntityRightPanelCard
              header={h}
              entityKind="attribute"
              isSessionNew={isSessionNew(attrId)}
              title={h.name || "—"}
              onRemove={() => {
                if (isOpen) setConstrainOpenId(null);
                onRemoveColumnScope(h, "attribute");
              }}
              isRemoveDisabled={isScopeLocked}
              subtitle={subtitleContent}
              errorIndicator={errorIcon}
              kgColumnProtoMap={kgColumnProtoMap}
              dataModelList={dataModelList}
              metricProtoMap={metricProtoMap}
              footer={isOpen && scopeIndex >= 0 ? (
                <InlineConstraintEditor
                  scope={scope}
                  isLoadingValues={isLoadingValues}
                  onSave={(op, vals) => {
                    onSaveConstraint(scopeIndex, op, vals);
                    setConstrainOpenId(null);
                  }}
                  onCancel={() => setConstrainOpenId(null)}
                />
              ) : undefined}
            />
          </GridItem>
        );
      })}
    </SimpleGrid>
  );

  const renderTimeCards = (times: Header[]) => (
    <SimpleGrid columns={3} spacing={2} mb={2} w="100%">
      {times.map(m => {
        if (!m.id) return null;
        return (
          <EntityRightPanelCard
            key={m.id}
            header={m}
            entityKind="time"
            isSessionNew={isSessionNew(m.id)}
            title={m.name || "—"}
            onRemove={() => onRemoveColumnScope(m, "time")}
            isRemoveDisabled={isScopeLocked}
            kgColumnProtoMap={kgColumnProtoMap}
            dataModelList={dataModelList}
            metricProtoMap={metricProtoMap}
          />
        );
      })}
    </SimpleGrid>
  );

  const renderBenchmarkCards = (benchmarks: Header[]) => (
    <SimpleGrid columns={3} spacing={2} mb={2} w="100%">
      {benchmarks.map(m => {
        if (!m.id) return null;
        return (
          <EntityRightPanelCard
            key={m.id}
            header={m}
            entityKind="benchmark"
            isSessionNew={isSessionNew(m.id)}
            title={m.name || "—"}
            onRemove={() => onRemoveBenchmark(m)}
            isRemoveDisabled={isScopeLocked}
            kgColumnProtoMap={kgColumnProtoMap}
            dataModelList={dataModelList}
            metricProtoMap={metricProtoMap}
          />
        );
      })}
    </SimpleGrid>
  );

  // ─── All-entities view ────────────────────────────────────────────────────

  const renderContent = () => {
    if (selectedCount === 0) {
      return (
        <Text fontSize="sm" color="gray.500" textAlign="center" py={6} mb={0}>
          Select entities from the left.
        </Text>
      );
    }

    return (
      <>
        {selectedMetricHeaders.length > 0 && (
          <CollapsibleSection
            type="metric"
            title="Metrics"
            count={selectedMetricHeaders.length}
            isOpen={isSectionOpen("metrics")}
            onToggle={() => toggleSection("metrics")}
          >
            {renderMetricCards(selectedMetricHeaders)}
          </CollapsibleSection>
        )}
        {selectedMeasures.length > 0 && (
          <CollapsibleSection
            type="measure"
            title="Measures"
            count={selectedMeasures.length}
            isOpen={isSectionOpen("measures")}
            onToggle={() => toggleSection("measures")}
          >
            {renderMeasureCards(selectedMeasures)}
          </CollapsibleSection>
        )}
        {attrScopes.length > 0 && (
          <CollapsibleSection
            type="attribute"
            title="Attributes"
            count={attrScopes.length}
            isOpen={isSectionOpen("attributes")}
            onToggle={() => toggleSection("attributes")}
          >
            <Box position="relative">
              {isSavingConstraint && (
                <Flex
                  position="absolute"
                  inset={0}
                  borderRadius="md"
                  bg="whiteAlpha.800"
                  align="center"
                  justify="center"
                  zIndex={10}
                >
                  <HStack spacing={2} px={3} py={2} borderRadius="md" bg="purple.50" borderWidth="1px" borderColor="purple.200">
                    <Spinner size="xs" color="purple.800" flexShrink={0} />
                    <Text fontSize="xs" fontWeight="500" color="purple.800" mb={0} lineHeight="short">
                      Fetching attribute values under new constraints…
                    </Text>
                  </HStack>
                </Flex>
              )}
              {renderAttributeCards(attrScopes)}
            </Box>
          </CollapsibleSection>
        )}
        {selectedTimeframes.length > 0 && (
          <CollapsibleSection
            type="time"
            title="Time"
            count={selectedTimeframes.length}
            isOpen={isSectionOpen("time")}
            onToggle={() => toggleSection("time")}
          >
            {renderTimeCards(selectedTimeframes)}
          </CollapsibleSection>
        )}
        {selectedBenchmarks.length > 0 && (
          <CollapsibleSection
            type="benchmark"
            title="Benchmarks"
            count={selectedBenchmarks.length}
            isOpen={isSectionOpen("benchmarks")}
            onToggle={() => toggleSection("benchmarks")}
          >
            {renderBenchmarkCards(selectedBenchmarks)}
          </CollapsibleSection>
        )}
      </>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Flex direction="column" h="100%" flex="1" minH={0}>
      {/* Header */}
      <Box px={4} py={2} borderBottomWidth="1px" borderColor="gray.100" flexShrink={0} bg="gray.50">
        <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
          <HStack align="center" gap={1.5} minW={0}>
            <Icon as={AppIcons.analystScope} boxSize={6} color="gray.900" flexShrink={0} aria-hidden />
            <Text fontWeight="700" color="gray.900" fontSize="md" mb={0} textAlign="left" noOfLines={2}>
              What this analyst can access
            </Text>
          </HStack>
        </Flex>
      </Box>

      {/* Body */}
      {isLoadingScope ? (
        <Flex flex="1" direction="column" align="center" justify="center" gap={3} py={10} bg="gray.50">
          <Spinner size="lg" color="purple.500" thickness="3px" speed="0.7s" />
          <Text fontSize="sm" color="gray.500" fontWeight="500" mb={0}>
            Loading data scope…
          </Text>
        </Flex>
      ) : (
        <Box flex="1" minH={0} overflowY="auto" px={3} py={2} sx={SCROLLBAR_SX} bg="gray.50">
          {renderContent()}
        </Box>
      )}
    </Flex>
  );
};

export default DataScopeRightPanel;
